import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import styles from "./Orders.module.css";
import Api from "../../utils/Api/Api";
import { money, shortDate } from "../../utils/format";
import { TopBar } from "../../components/TopBar/TopBar";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { StatusChip, orderState } from "../../components/StatusChip/StatusChip";
import { ErrorFallback } from "../../components/Error/Error";
import { GiftTag, GiftClaimedBanner, isReceivedGift } from "../../components/GiftTag/GiftTag";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

const FILTERS = ["All", "Active", "Expired"];

const BagIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8h12l1 12H5z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
);

/**
 * My orders, rebuilt from design/Purchase Flow.dc.html (isOrders block).
 * Three fixed filters; "Active" includes orders still being activated.
 */
export const Orders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const claimedGift = location.state?.giftClaimed || null;
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Api.GetOrderDetails()
      .then(({ data }) => {
        if (cancelled) return;
        setOrders(Array.isArray(data?.results) ? data.results : []);
      })
      .catch((err) => {
        console.error("Orders failed to load:", err);
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () =>
      orders.map((o) => {
        const plan = o.subscription_plan || {};
        const state = orderState(o);
        return {
          id: o.id,
          state,
          logo: plan.image_url || FleekIcon,
          brand: plan.service_name || o.product_name || "",
          plan: o.product_digital ? plan.plan_name : o.product_name,
          // A received gift was paid for by someone else: no price.
          amount: isReceivedGift(o) ? "" : money(o.payment?.amount ?? o.actual_discounted_cost ?? o.cost, o.details?.currency_type),
          gift: o.gift || null,
          date: shortDate(o.updated_at || o.created_at),
        };
      }),
    [orders]
  );

  const visible = rows.filter((r) =>
    filter === "All" ? true : filter === "Active" ? r.state !== "expired" : r.state === "expired"
  );

  if (hasError) return <ErrorFallback />;

  return (
    <div className={styles.page}>
      <TopBar title="My orders" backTo="/account" />
      <div className={styles.shell}>
        {!loading && <GiftClaimedBanner claimed={claimedGift} />}
        {loading ? (
          <>
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<BagIcon />}
            title="No orders yet"
            body="Plans you buy will show up here."
            action="Browse brands"
            onAction={() => navigate("/marketplace")}
          />
        ) : (
          <>
            <div className={styles.filters} role="group" aria-label="Filter orders">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`${styles.filter} ${filter === f ? styles.filterOn : ""}`}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className={styles.list}>
              {visible.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={styles.row}
                  onClick={() => navigate(`/orderdetail/${r.id}`)}
                >
                  <img
                    className={styles.logo}
                    src={r.logo}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = FleekIcon;
                    }}
                  />
                  <span className={styles.text}>
                    <span className={styles.brand}>{r.brand}</span>
                    {r.plan && <span className={styles.plan}>{r.plan}</span>}
                    <span className={styles.chip}>
                      <StatusChip state={r.state} />
                      {r.gift && <GiftTag gift={r.gift} />}
                    </span>
                  </span>
                  <span className={styles.right}>
                    <span className={styles.amount}>{r.amount}</span>
                    <span className={styles.date}>{r.date}</span>
                  </span>
                </button>
              ))}
              {visible.length === 0 && (
                <EmptyState icon={<BagIcon />} title={`No ${filter.toLowerCase()} orders`} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Orders;
